"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Phone, Mail, ShieldAlert, Lock, Terminal, Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const customInputClasses = "border-x-0 border-t-0 border-b border-border rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-b-primary focus-visible:border-b-2 bg-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
function SecureForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="221899" className="flex flex-col items-center justify-center p-12 text-center border border-border bg-card/30 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
        <Lock className="size-12 text-primary mb-6" />
        <h3 className="text-2xl font-serif font-bold mb-2 text-foreground uppercase">SOLICITUD PROCESADA</h3>
        <p className="text-muted-foreground mb-8 font-default text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default rounded-none">
          NUEVA SOLICITUD
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ej: Juan Pérez" className={customInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Correo electrónico requerido",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Formato de correo inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@ejemplo.com" className={customInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Teléfono de Contacto</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+56 9 1234 5678" className={customInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Fundo o Empresa</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de su organización" className={customInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_type" rules={{
      required: "Seleccione un equipo"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Maquinaria Requerida</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`${customInputClasses} w-full border-b border-border`}>
                  <SelectValue placeholder="Seleccione maquinaria" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-none border-border bg-card">
                <SelectItem value="Tractor 70hp">Tractor 70 HP</SelectItem>
                <SelectItem value="Tractor 100hp">Tractor 100 HP</SelectItem>
                <SelectItem value="Tractor 110hp">Tractor 110 HP</SelectItem>
                <SelectItem value="Grua Horquilla 4x4">Grúa Horquilla 4x4</SelectItem>
                <SelectItem value="Grua Horquilla 4x2">Grúa Horquilla 4x2</SelectItem>
                <SelectItem value="Autocargable">Autocargable</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="brief" rules={{
      required: "Detalles del arriendo requeridos"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-muted-foreground text-xs uppercase tracking-widest">Detalles del Arriendo</FormLabel>
            <FormControl>
              <Textarea placeholder="Indique duración estimada (mínimo 1 mes), ubicación del trabajo y requerimientos específicos..." className={`min-h-[120px] ${customInputClasses} py-3`} {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs opacity-70">
              Arriendos disponibles desde 1 mes en adelante con despacho a terreno.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 text-destructive font-default text-sm">
          <Terminal className="inline-block mr-2 size-4" />
          ERROR: {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-serif tracking-widest relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground rounded-none border border-primary/50 shadow-lg flex items-center justify-center cursor-pointer">
          {isSubmitting ? <Loader2 className="mr-2 size-5 animate-spin" /> : <ShieldAlert className="mr-2 size-5" />}
          {isSubmitting ? "PROCESANDO..." : "SOLICITAR COTIZACIÓN"}
        </Button>
      </div>
    </Form>;
}
export default function BriefOurAnalysts() {
  return <section id="solicita-tu-maquinaria" className="bg-background text-foreground py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            
            <div className="lg:col-span-5 flex flex-col space-y-10">
              <div>
                <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 tracking-tight uppercase">
                  Potencia tu Cosecha
                </h2>
              </div>

              <div className="space-y-6 font-default text-sm">
                <div className="flex items-start space-x-4 p-4 border border-border/50 bg-card/10 backdrop-blur-sm shadow-sm">
                  <MapPin className="size-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="text-muted-foreground mb-1 uppercase tracking-wider text-xs font-serif">Ubicación Central</div>
                    <div className="text-foreground">Los Lingues, San Fernando, O'Higgins</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 border border-border/50 bg-card/10 backdrop-blur-sm shadow-sm">
                  <Phone className="size-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="text-muted-foreground mb-1 uppercase tracking-wider text-xs font-serif">Atención Comercial</div>
                    <div className="text-foreground">+56 98628 2093<br />+56 9 9929 4934</div>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 border border-border/50 bg-card/10 backdrop-blur-sm shadow-sm">
                  <Mail className="size-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="text-muted-foreground mb-1 uppercase tracking-wider text-xs font-serif">Consultas Directas</div>
                    <div className="text-foreground">arriendoarrels@gmail.com</div>
                  </div>
                </div>
              </div>

              <div className="relative h-64 w-full border border-border/50 bg-card/10 overflow-hidden group shadow-xl">
                <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647820/img/forklift_mud_operation.png)] opacity-80 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-primary/5" />
                <div className="absolute top-1/2 left-1/2 w-[200%] h-[200%] -translate-x-1/2 -translate-y-1/2 bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0deg,transparent_270deg,var(--color-primary)_360deg)] opacity-10 animate-[spin_8s_linear_infinite]" />
                
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                  <div className="size-4 bg-primary rounded-full animate-ping absolute" />
                  <div className="size-2 bg-primary rounded-full relative z-10" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="p-8 md:p-12 border border-border/50 bg-card/10 backdrop-blur-md relative shadow-2xl shadow-black/10">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/50" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/50" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/50" />

                <div className="mb-8 border-b border-border/50 pb-4 flex items-center justify-between">
                  <h3 className="text-xl font-serif font-bold text-foreground uppercase tracking-wider">Solicita tu Maquinaria</h3>
                  <Terminal className="size-5 text-muted-foreground" />
                </div>

                <FormProvider formId="machinery_request_home" sectionName="Solicita tu Maquinaria" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                service_type: "",
                brief: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                service_type: {
                  required: true
                },
                brief: {
                  required: true
                }
              }} submitText="SOLICITAR COTIZACIÓN" successMessage="Solicitud recibida. Un ejecutivo de ARRELS se contactará con usted para coordinar el despacho y contrato.">
                  <SecureForm />
                </FormProvider>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}